import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZE_OPTIONS } from './usePageSize';
import type { PageSizeOption } from './usePageSize';

export interface PaginationFooterProps {
  /**
   * 1-indexed current page — pass the URL-derived page (`pageFromParams`), NOT
   * `meta.page`.
   *
   * `meta.page` echoes what was sent to the API, which is 0-based
   * (`Skip(page * pageSize)`). Feeding it here made every pager read "0 / 1",
   * and worse, the boundary checks below assume 1-indexing: with a 0-based
   * value Prev would lock on page 2 (`1 <= 1`) and Next would run one page past
   * the end. Invisible until a list exceeded one page.
   */
  page: number;
  /** Total page count (matches `meta.totalPage`). */
  totalPages: number;
  /**
   * Current page size. When provided alongside `onPageSizeChange`, renders the
   * 80px page-size Select to the left of the prev/next buttons.
   */
  pageSize?: PageSizeOption;
  /**
   * Page-size change callback. The Select onChange casts the raw HTML value
   * to `PageSizeOption` (safe because the rendered options are constrained
   * to `PAGE_SIZE_OPTIONS`).
   */
  onPageSizeChange?: (size: PageSizeOption) => void;
  onPrev: () => void;
  onNext: () => void;
  /** Disables all controls during fetches; the buttons additionally disable at page boundaries. */
  loading: boolean;
  /**
   * Required when rendering the page-size Select — provides a unique base for
   * the FormControl `labelId` / `id` (e.g. `'customers'` →
   * `'customers-page-size-label'` / `'customers-page-size'`).
   */
  pageSizeId?: string;
  /**
   * Optional override for the inline content between the prev and next
   * IconButtons. Default: `<Typography variant="body2">{page / totalPages}</Typography>`.
   * Pages with extra UX (e.g. TowRequestsList / ClaimsList page-jump
   * `<TextField type="number">` plus a `t('pageOf', { total })` template)
   * pass a fragment containing both elements here.
   */
  pageIndicator?: React.ReactNode;
}

/**
 * Standard list-page pagination footer: an optional 80px page-size Select +
 * prev IconButton + `page / totalPage` indicator + next IconButton, all
 * laid out in a horizontal `<Box>`. Tooltip-wrapped IconButtons use
 * `<span>`-wrap so MUI fires the tooltip even when the button is disabled.
 *
 * Lives here so the 14 list pages stop redeclaring the identical 55-line
 * footer block (with per-page ARIA id strings as the only variable). Pairs
 * with `usePageSize` (slice 796) for the source of `pageSize` and
 * `usePaginationShortcuts` (slice 704) for the keyboard binding.
 *
 * Pages without a page-size selector (RolesList, TermsConditionsList) omit
 * the `pageSize` / `onPageSizeChange` props — the Select is skipped.
 */
/**
 * Prev/next buttons as the mockup's `.pager .pg span`: a bordered rounded
 * square rather than a bare icon.
 *
 * The mockup draws these at 28px. We keep MUI's `size="small"` box (34px) —
 * 28px is below a comfortable hit target, and SPEC.md's accessibility floor
 * wins over exact pixel-matching on an interactive control.
 */
const pagerButtonSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: '8px',
  '&.Mui-disabled': { borderColor: 'divider', opacity: 0.5 },
} as const;

export function PaginationFooter({
  page,
  totalPages,
  pageSize,
  onPageSizeChange,
  onPrev,
  onNext,
  loading,
  pageSizeId,
  pageIndicator,
}: PaginationFooterProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {pageSize !== undefined && onPageSizeChange !== undefined && pageSizeId !== undefined && (
        <FormControl size="small" sx={{ width: 80 }} disabled={loading}>
          <InputLabel id={`${pageSizeId}-page-size-label`}>
            {t('OPTIONS-SELECT.pageSize', { defaultValue: 'Page size' })}
          </InputLabel>
          <Select
            labelId={`${pageSizeId}-page-size-label`}
            id={`${pageSizeId}-page-size`}
            value={pageSize}
            label={t('OPTIONS-SELECT.pageSize', { defaultValue: 'Page size' })}
            onChange={(e) => onPageSizeChange(Number(e.target.value) as PageSizeOption)}
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Tooltip title={t('LOOKUP.pagination.prev')}>
        <span>
          <IconButton
            size="small"
            onClick={onPrev}
            disabled={loading || page <= 1}
            aria-label={t('LOOKUP.pagination.prev')}
            sx={pagerButtonSx}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
        </span>
      </Tooltip>

      {pageIndicator ?? (
        <Typography
          sx={{ fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
        >
          {`${page} / ${totalPages}`}
        </Typography>
      )}

      <Tooltip title={t('LOOKUP.pagination.next')}>
        <span>
          <IconButton
            size="small"
            onClick={onNext}
            disabled={loading || page >= totalPages}
            aria-label={t('LOOKUP.pagination.next')}
            sx={pagerButtonSx}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
